import {WriteButton} from '../common/buttons/WriteActions';
import {useEffect, useState} from 'react';
import {
    Alert,
    AlertColor,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormGroup,
    IconButton,
    InputAdornment,
    Radio,
    RadioGroup,
    Stack,
    TextField,
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SettingsIcon from '@mui/icons-material/Settings';
import SearchOffIcon from '@mui/icons-material/SearchOff';

import {DateObject} from 'react-multi-date-picker';
import {LocalizedCalendar as Calendar} from '../common/dates/LocalizedCalendar';
import {getCalendarDate} from '@mybake/ui/common/helpers';
import {getMedia} from '../../common/mediaQuery';
import {IOrderSearch} from '../../models/Order/IOrderSearch';
import {orderAPI} from '../../services/OrderServices';
import MySnackBar from '../common/snackBars/MySnackBar';
import useDebounce from '../../hooks/useDebounce';
import {useAppTranslation} from "@mybake/ui/hooks/useAppTranslation";
import {OrderTable} from "./order/OrderTable";
import {useNavigate} from "react-router-dom";
import {OrdersGroupByDayGuidDto} from '@mybake/ui/models/Order/IOrderGuid';
import {getApiErrorMessage} from '../../common/apiErrors';
import {PageHeader} from '../common/layout/PageHeader';
import {Surface} from '../common/layout/Surface';
import {ContentSkeleton} from '../common/loading/ContentSkeleton';

const OrdersHOC = () => {
    const {t} = useAppTranslation();
    const navigate = useNavigate();
    const {isFullHD, isTablet} = getMedia();

    const [searchResult, setSearchResult] = useState<OrdersGroupByDayGuidDto[] | undefined>();
    const [searchBar, setSearchBar] = useState<string>('');
    const [searchOptions, setSearchOptions] = useState<boolean>(false);
    const [isMultipleDate, setIsMultipleDate] = useState<boolean>(false);
    const [isDateRange, setIsDateRange] = useState<boolean>(false);
    const [closedOrders, setClosedOrders] = useState<boolean>(false);
    const [isRecipeName, setIsRecipeName] = useState<boolean>(false);
    const [calendarDialogOpen, setCalendarDialogOpen] = useState<boolean>(false);
    const [dateRange, setDateRange] = useState<DateObject[]>([]);
    const [multipleDates, setMultipleDates] = useState<DateObject[]>([]);
    const [fetchedOrderSnackBar, setFetchedOrderSnackBar] = useState<boolean>(false);
    const [fetchedOrderErrorSnackBar, setFetchedOrderErrorSnackBar] = useState<boolean>(false);
    const [searchOrderSnackBar, setSearchOrderSnackBar] = useState<boolean>(false);
    const [searchOrderErrorSnackBar, setSearchOrderErrorSnackBar] = useState<boolean>(false);

    const successColor: AlertColor = 'success';
    const errorColor: AlertColor = 'error';
    const {
        data: ordersV2,
        isLoading: ordersIsLoadingV2,
        isError: ordersIsErrorV2,
        isSuccess: ordersIsSuccessV2,
        error: ordersErrorV2,
    } = orderAPI.useGetOrdersV2Query(false);
    const [orderSearch, {
        data: orderSearchResultData,
        isError: orderSearchIsError,
        isLoading: orderSearchIsLoading,
        isSuccess: orderSearchIsSuccess,
        error: orderSearchError,
    }] =
        orderAPI.useLazySearchOrdersV2Query();

    const debouncedValue = useDebounce(searchBar, 1000);

    const {
        data: hasUnreviewedOrders,
        isLoading: hasUnreviewedOrdersIsLoading,
        isError: hasUnreviewedOrdersIsError,
        isSuccess: hasUnreviewedOrdersIsSuccess,
    } = orderAPI.useHasUnreviewedOrdersV2Query();

    const search = () => {
        const searchDTO: IOrderSearch = {
            query: searchBar,
            closedOrders: closedOrders,
            isDateRange: isDateRange,
            isMultipleDate: isMultipleDate,
            isRecipeName: isRecipeName
        };
        orderSearch(searchDTO).then((res) => {
            if ('data' in res) {
                if (res.data && res.data.length > 0) {
                    setSearchResult(res.data);
                } else {
                    setSearchResult([]);
                }
            }
        });
    };

    const setDialogOpen = () => {
        setCalendarDialogOpen(!calendarDialogOpen);
    };

    const searchRange = () => {
        if (dateRange.length === 2) {
            setSearchBar(`${getCalendarDate(dateRange[0].format())} ~ ${getCalendarDate(dateRange[1].format())}`);
            setCalendarDialogOpen(false);
        }
    };

    const searchMultipleDates = () => {
        const dates: string[] = [];
        multipleDates.forEach((el) => dates.push(`${getCalendarDate(el.format())}`));
        setSearchBar(dates.toString());
        setCalendarDialogOpen(false);
    };

    const setRange = (dateObjects: DateObject[]) => {
        if (dateObjects.length === 2) {
            setDateRange(dateObjects);
        }
    };
    const setMultiple = (dateObjects: DateObject[]) => {
        setMultipleDates([...dateObjects]);
    };

    const handleChangeSearch = (event: any) => {
        if (event.type === 'click') {
            setSearchBar('');
            setClosedOrders(false);
            setIsDateRange(false);
            setIsMultipleDate(false);
            setIsRecipeName(false);
            setDateRange([]);
            setMultipleDates([]);
            setSearchOptions(false);
        } else {
            setSearchBar(event.target.value);
        }
    };

    const dateRangeCheckBox = (value: boolean) => {
        setIsDateRange(value);
        setIsMultipleDate(false);
        setIsRecipeName(false);
        setMultipleDates([]);
        setSearchBar('');
    };

    const multipleDatesCheckBox = (value: boolean) => {
        setIsDateRange(false);
        setIsMultipleDate(value);
        setIsRecipeName(false);
        setDateRange([]);
        setSearchBar('');
    };

    const searchUnReviewedOrders = () => {
        // TODO: phrase 'not reviewed' need use multilanguage from dictionary when this feature was implemented in back-end
        setSearchBar('not reviewed');
    };

    useEffect(() => {
        if (searchBar) {
            search();
        } else {
            setSearchBar('');
            setSearchResult(undefined);
        }
    }, [debouncedValue, closedOrders]);

    useEffect(() => {
        if (!ordersIsLoadingV2 && !ordersIsErrorV2 && ordersIsSuccessV2) setFetchedOrderSnackBar(true);
        if (ordersIsErrorV2) setFetchedOrderErrorSnackBar(true);
        if (orderSearchIsLoading && !orderSearchIsError) setSearchOrderSnackBar(true);
        if (orderSearchIsError) setSearchOrderErrorSnackBar(true);
    }, [ordersIsLoadingV2, ordersIsErrorV2, ordersIsSuccessV2, orderSearchIsError, orderSearchIsLoading]);

    useEffect(() => {
        if (orderSearchResultData === null && searchBar.length >= 3) {
            setSearchBar('');
            setSearchResult(undefined);
        } else {
            setSearchResult(orderSearchResultData);
        }
    }, [orderSearchResultData]);

    return (
        <Box sx={{...(isFullHD ? {minHeight: '900px'} : {})}}>
            <PageHeader
                title={t('navigation:nav_orders')}
                action={<WriteButton variant="contained" onClick={() => navigate('/app/orders/new')}>{t('orders:create_order')}</WriteButton>}
            />

            <Surface sx={{p: {xs: 1.5, sm: 2}, mb: 2}}>
                <Box sx={{display: 'flex', gap: 1, alignItems: 'center'}}>
                    <TextField
                        id={'searchInput'}
                        label={t('common:search')}
                        variant="outlined"
                        size="small"
                        value={searchBar}
                        fullWidth
                        onChange={handleChangeSearch}
                        sx={{'.MuiInputBase-input': {pr: 0}, minWidth: 0}}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    {searchBar && (
                                        <IconButton aria-label={t('common:clear_search')} onClick={(e) => handleChangeSearch(e)}
                                                    edge="end" sx={{mr: '0px', ml: '0px'}}>
                                            <SearchOffIcon/>
                                        </IconButton>
                                    )}
                                </InputAdornment>
                            ),
                        }}
                    />

                    <IconButton
                        aria-label={t('orders:search_options')}
                        onClick={() => {
                            setSearchOptions(!searchOptions);
                        }}
                        sx={{
                            width: 48,
                            height: 48,
                            flexShrink: 0,
                            border: 1,
                            borderColor: searchOptions ? 'primary.main' : 'divider',
                            bgcolor: searchOptions ? 'rgba(154, 90, 60, 0.08)' : 'background.paper',
                        }}
                    >
                        <SettingsIcon/>
                    </IconButton>
                </Box>
                {searchOptions && (
                    <Box sx={{mt: 2, pt: 2, borderTop: 1, borderColor: 'divider'}}>
                        <FormGroup>
                            <Stack direction={isTablet ? 'column' : 'row'} spacing={1} alignSelf="center">
                                <FormControlLabel
                                    control={<Checkbox checked={closedOrders} value={closedOrders}
                                                       onClick={() => setClosedOrders(!closedOrders)}/>}
                                    label={t('orders:closed_orders')}
                                />
                                <FormControlLabel
                                    control={<Checkbox checked={isRecipeName} value={isRecipeName}
                                                       onClick={() => setIsRecipeName(!isRecipeName)}/>}
                                    label={t('orders:by_recipe_name')}
                                />
                                <FormControl sx={{paddingLeft: '11px'}}>
                                    <Stack direction="row" spacing={1} alignSelf="center">
                                        <RadioGroup row>
                                            <FormControlLabel
                                                onChange={() => dateRangeCheckBox(!isDateRange)}
                                                control={<Radio checked={isDateRange} value={isDateRange}/>}
                                                label={t('orders:date_range_search')}
                                            />
                                            <FormControlLabel
                                                onChange={() => multipleDatesCheckBox(!isMultipleDate)}
                                                control={<Radio checked={isMultipleDate} value={isMultipleDate}/>}
                                                label={t('orders:multiple_dates_search')}
                                            />
                                        </RadioGroup>
                                    </Stack>
                                </FormControl>

                                <Button
                                    size="small"
                                    variant="text"
                                    onClick={setDialogOpen}
                                    endIcon={<CalendarMonthIcon/>}
                                >
                                    {t('orders:open_calendar')}
                                </Button>
                            </Stack>
                        </FormGroup>
                    </Box>
                )}
            </Surface>

            {hasUnreviewedOrders && (
                <Alert severity="info" onClick={searchUnReviewedOrders}
                       sx={{mb: 2, cursor: 'pointer', color: 'primary.dark', bgcolor: 'rgba(154, 90, 60, 0.08)', borderColor: 'rgba(154, 90, 60, 0.2)'}}>
                    Has unreviewed orders
                </Alert>
            )}
            {(ordersIsLoadingV2 || orderSearchIsLoading) && <ContentSkeleton variant="list" rows={6}/>}
            {ordersIsErrorV2 && <Alert severity="error" sx={{mb: 1}}>{getApiErrorMessage(ordersErrorV2, t)}</Alert>}
            {!ordersIsLoadingV2 && ordersV2 && !searchResult && <Surface><OrderTable orders={ordersV2}/></Surface>}
            {!orderSearchIsLoading && searchResult && <Surface><OrderTable orders={searchResult}/></Surface>}
            <Dialog open={calendarDialogOpen} onClose={setDialogOpen}>
                <DialogTitle alignSelf="center">
                    <Box component={'span'} sx={isTablet ? {fontSize: '1rem'} : {}}>
                        {isDateRange ? t('orders:select_date_range_for_the_search') : t('orders:select_dates_for_the_search')}
                    </Box>
                </DialogTitle>
                <DialogContent sx={{alignSelf: 'center'}}>
                    {isDateRange ? (
                        <Calendar
                            value={dateRange}
                            onChange={(dateObjects) => {
                                setRange(dateObjects as DateObject[]);
                            }}
                            range
                            rangeHover
                        />
                    ) : (
                        <Calendar
                            value={multipleDates}
                            onChange={(dateObjects) => {
                                setMultiple(dateObjects as DateObject[]);
                            }}
                            multiple
                        />
                    )}
                </DialogContent>
                <DialogActions sx={{alignSelf: 'center', mb: '10px'}}>
                    <Button variant="outlined" onClick={isDateRange ? searchRange : searchMultipleDates}>
                        {t('common:search')}
                    </Button>
                </DialogActions>
            </Dialog>
            <MySnackBar
                duration={5000}
                openSnackBar={fetchedOrderSnackBar}
                handleClose={() => setFetchedOrderSnackBar(false)}
                severity={successColor}
                alertText="All orders fetched!"
            />
            <MySnackBar
                duration={5000}
                openSnackBar={fetchedOrderErrorSnackBar}
                handleClose={() => setFetchedOrderErrorSnackBar(false)}
                severity={errorColor}
                alertText={getApiErrorMessage(ordersErrorV2, t)}
            />
            <MySnackBar duration={5000} openSnackBar={searchOrderSnackBar}
                        handleClose={() => setSearchOrderSnackBar(false)} severity={successColor}
                        alertText="Search success!"/>
            <MySnackBar
                duration={5000}
                openSnackBar={searchOrderErrorSnackBar}
                handleClose={() => setSearchOrderErrorSnackBar(false)}
                severity={errorColor}
                alertText={getApiErrorMessage(orderSearchError, t)}
            />
        </Box>
    );
};

export default OrdersHOC;
